/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import type { KaryaFileRecord } from '@karya/core';
import { createApi, fetchBaseQuery, type FetchBaseQueryArgs } from '@reduxjs/toolkit/query/react';

import { setKaryaWorkerIdToken } from '../PrepareHeader';

const BASE_FILESERVER_URL = import.meta.env.VITE_KARYA_FILESERVER;
if (!BASE_FILESERVER_URL) {
  throw new Error('Missing required env: VITE_KARYA_FILESERVER');
}

export type GetFileUploadSasUrlArgs = {
  access_code: string;
  filename: string;
};

export type GetFileDownloadSasUrlArgs = {
  access_code: string;
  filename: string;
};

export type FileSasUrlResponse = {
  sasURL: string;
};

export type FileDownloadSasUrlResponse = FileSasUrlResponse & {
  checksum: string | null;
  algorithm: KaryaFileRecord['algorithm'];
  timestamp: string | null;
};

export type AcknowledgeFileUploadSuccessArgs = {
  access_code: string;
  filename: string;
  checksum: string;
  timestamp: string;
};

/**
 * Mutable files API singleton for dependency injection.
 *
 * Must be initialized via `buildFilesApi(...)` or `setFilesApi(...)` before any
 * consumer imports and uses it. Ownership stays with the service wiring layer;
 * consumers should read from it but not import and mutate it directly.
 */
export let filesApi: ReturnType<typeof buildFilesApi>;

/**
 * Builds the files API with an optional injected fetch implementation.
 *
 * This is the factory used by the wiring layer so tests or alternate runtimes
 * can supply their own transport while keeping the exported singleton stable.
 */
export function buildFilesApi(fetchFn?: FetchBaseQueryArgs['fetchFn']) {
  return createApi({
    reducerPath: 'fileserver',
    baseQuery: fetchBaseQuery({
      fetchFn,
      baseUrl: BASE_FILESERVER_URL,
      prepareHeaders: setKaryaWorkerIdToken,
    }),

    endpoints: (build) => ({
      getFileUploadSasUrl: build.mutation<FileSasUrlResponse, GetFileUploadSasUrlArgs>({
        query: ({ access_code, filename }) => ({
          url: `/fileserver/v1/worker/file_upload_sas_url?${new URLSearchParams({ filename }).toString()}`,
          method: 'GET',
          headers: {
            access_code,
          },
        }),
      }),

      acknowledgeFileUploadSuccess: build.mutation<KaryaFileRecord, AcknowledgeFileUploadSuccessArgs>({
        query: ({ access_code, ...body }) => ({
          url: '/fileserver/v1/worker/file_upload_success',
          method: 'POST',
          headers: {
            access_code,
          },
          body,
        }),
      }),

      getFileDownloadSasUrl: build.mutation<FileDownloadSasUrlResponse, GetFileDownloadSasUrlArgs>({
        query: ({ access_code, filename }) => ({
          url: `/fileserver/v1/worker/file_download_sas_url?${new URLSearchParams({ filename }).toString()}`,
          method: 'GET',
          headers: {
            access_code,
          },
        }),
      }),
    }),
  });
}

/**
 * Replaces the exported files API singleton.
 *
 * The service bootstrap uses this to swap in the active instance before any
 * store or hook consumers read from `filesApi`.
 */
export function setFilesApi(value: ReturnType<typeof buildFilesApi>) {
  filesApi = value;
}

setFilesApi(buildFilesApi());
